import { RadioGroup } from "@headlessui/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ThirdPartyEnabledEnum } from "../../apiClient/generated";
import { useDataProvidersApi, useNotificationsApi } from "../../hooks";
import { CustomSwitch } from "../../ui/CustomSwitch";
import { LoadingIndicator } from "../MapV2/ui/LoadingIndicator";

const EmissionOptions = [
    {
        key: ThirdPartyEnabledEnum.All,
        label: "All",
        description: "All new notifications will be sent individually.",
    },
    {
        key: ThirdPartyEnabledEnum.BatchDigest,
        label: "Digest",
        description:
            "New notifications will be batched and a digest will be sent.",
    },
    {
        key: ThirdPartyEnabledEnum.None,
        label: "None",
        description: "We won't send any notifications for this.",
    },
];

interface OptionSelectorProps {
    options: {
        key: string;
        label: string;
        description?: string;
    }[];
    value: string;
    onChange: (newValue: string) => void;
}

const OptionSelector = (props: OptionSelectorProps) => (
    <RadioGroup
        className="flex gap-1 bg-ae-slate-200 w-96 h-fit p-1 rounded-lg"
        value={props.value}
        onChange={props.onChange}
    >
        {props.options.map((option) => (
            <RadioGroup.Option
                key={option.key}
                value={option.key}
                className={`
                    cursor-pointer py-2 px-4 rounded-lg w-full
                    flex items-center justify-center
                    ${props.value === option.key ? "bg-white" : ""}
                `}
                title={option.description}
            >
                <span className={props.value === option.key && "font-bold"}>
                    {option.label}
                </span>
            </RadioGroup.Option>
        ))}
    </RadioGroup>
);

const SettingsSection = (props: { title: string; children: ReactNode }) => (
    <>
        <hr className="my-4" />
        <h3 className="font-md">{props.title}</h3>
        <div>{props.children}</div>
    </>
);

interface EmissionSettingsProps {
    company?: number;
}

export const EmissionSettings = (props: EmissionSettingsProps) => {
    const apiClient = useNotificationsApi();
    const dataProvidersApiClient = useDataProvidersApi();

    // Fetch additional data
    const enabledDataProvidersQuery = useQuery({
        queryKey: ["enabledDataProviders", props.company],
        queryFn: async () => {
            return await dataProvidersApiClient.dataProvidersList({
                isEnabled: true,
                company: props.company,
            });
        },
        staleTime: Infinity,
    });

    const notificationGroupsQuery = useQuery({
        queryKey: ["notificationGroup", props.company],
        queryFn: async () => {
            const response = await apiClient.notificationsGroupsList({
                owner: props.company,
                pageSize: 30,
            });
            return response.results;
        },
    });

    // Notification settings
    const notificationSettingsQuery = useQuery({
        queryKey: ["emissionNotificationSettings", props.company],
        queryFn: async () => {
            const response = await apiClient.notificationsEmissionsList({
                owner: props.company,
                pageSize: 1,
            });
            return response.results[0];
        },
    });

    // Save changes fn
    const saveChanges = async (field: string, value: string | boolean) => {
        await apiClient.notificationsEmissionsPartialUpdate({
            id: notificationSettingsQuery.data?.id,
            patchedEmissionNotificationSettingsRequest: {
                [field]: value,
            },
        });
        notificationSettingsQuery.refetch();
    };

    const toggleProvider = async (providerId: string) => {
        let newValues = [
            ...notificationSettingsQuery.data.thirdPartyExcludedProviders,
        ];

        if (newValues.includes(providerId)) {
            newValues = newValues.filter((i) => i !== providerId);
        } else {
            newValues.push(providerId);
        }

        await apiClient.notificationsEmissionsPartialUpdate({
            id: notificationSettingsQuery.data?.id,
            patchedEmissionNotificationSettingsRequest: {
                thirdPartyExcludedProviders: newValues,
            },
        });
        notificationSettingsQuery.refetch();
    };

    if (
        notificationSettingsQuery.isLoading ||
        notificationGroupsQuery.isLoading ||
        enabledDataProvidersQuery.isLoading
    ) {
        return <LoadingIndicator />;
    }

    return (
        <div className="mt-2 text-sm">
            <div className="mt-4">
                <SettingsSection title="EPA Super Emitter Program ">
                    <div className="flex gap-2">
                        <div className="mt-1 text-ae-slate-600">
                            Select if how you'd like to be notified.
                        </div>

                        <OptionSelector
                            options={[EmissionOptions[0], EmissionOptions[2]]}
                            value={
                                notificationSettingsQuery.data?.epaSepEnabled
                            }
                            onChange={(value) =>
                                saveChanges("epaSepEnabled", value)
                            }
                        />

                        <div className="my-1 text-ae-slate-600">
                            Who should be notified?
                        </div>
                        <select
                            className="min-w-64 rounded p-2 h-fit"
                            value={notificationSettingsQuery.data?.epaSepGroup}
                            onChange={(e) => {
                                saveChanges("epaSepGroup", e.target.value);
                            }}
                        >
                            <option
                                disabled
                                selected={
                                    notificationSettingsQuery.data
                                        ?.epaSepGroup === undefined
                                }
                            ></option>
                            {notificationGroupsQuery.data?.map((group) => (
                                <option key={group.id} value={group.id}>
                                    {group.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="flex items-center gap-2 w-1/2">
                        <div className="mt-1 text-ae-slate-600 w-2/3">
                            Send reminders about EPA SEP events near deadlines
                        </div>

                        <CustomSwitch
                            checked={
                                notificationSettingsQuery.data?.epaSepReminders
                            }
                            onChange={(val) => {
                                saveChanges("epaSepReminders", val);
                            }}
                            size="lg"
                        />
                    </div>
                </SettingsSection>

                <SettingsSection title="Company Monitoring">
                    <div className="flex gap-2">
                        <div className="mt-1 text-ae-slate-600">
                            Select if how you'd like to be notified.
                        </div>

                        <OptionSelector
                            options={EmissionOptions}
                            value={
                                notificationSettingsQuery.data
                                    ?.selfReportedEnabled
                            }
                            onChange={(value) =>
                                saveChanges("selfReportedEnabled", value)
                            }
                        />

                        <div className="my-1 text-ae-slate-600">
                            Who should be notified?
                        </div>
                        <select
                            className="min-w-64 rounded p-2 h-fit"
                            value={
                                notificationSettingsQuery.data
                                    ?.selfReportedGroup
                            }
                            onChange={(e) => {
                                saveChanges(
                                    "selfReportedGroup",
                                    e.target.value,
                                );
                            }}
                        >
                            <option
                                disabled
                                selected={
                                    notificationSettingsQuery.data
                                        ?.selfReportedGroup === undefined
                                }
                            ></option>
                            {notificationGroupsQuery.data?.map((group) => (
                                <option key={group.id} value={group.id}>
                                    {group.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </SettingsSection>

                <SettingsSection title="3rd Party">
                    <div className="flex gap-2">
                        <div className="mt-1 text-ae-slate-600">
                            Select if how you'd like to be notified.
                        </div>

                        <OptionSelector
                            options={EmissionOptions}
                            value={
                                notificationSettingsQuery.data
                                    ?.thirdPartyEnabled
                            }
                            onChange={(value) =>
                                saveChanges("thirdPartyEnabled", value)
                            }
                        />

                        <div className="my-1 text-ae-slate-600">
                            Who should be notified?
                        </div>

                        <select
                            className="min-w-64 rounded p-2 h-fit"
                            value={
                                notificationSettingsQuery.data?.thirdPartyGroup
                            }
                            onChange={(e) => {
                                saveChanges("thirdPartyGroup", e.target.value);
                            }}
                        >
                            <option
                                disabled
                                selected={
                                    notificationSettingsQuery.data
                                        ?.thirdPartyGroup === undefined
                                }
                            ></option>

                            {notificationGroupsQuery.data?.map((group) => (
                                <option key={group.id} value={group.id}>
                                    {group.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="my-1 text-ae-slate-600">
                        Which providers should trigger alerts?
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        {enabledDataProvidersQuery.data?.results.map(
                            (provider) => (
                                <label
                                    key={provider.id}
                                    className="flex gap-2 items-center whitespace-nowrap line-clamp-1"
                                >
                                    <input
                                        type="checkbox"
                                        className="rounded text-ae-blue-500"
                                        checked={
                                            !notificationSettingsQuery.data?.thirdPartyExcludedProviders.includes(
                                                provider.id,
                                            )
                                        }
                                        onChange={() =>
                                            toggleProvider(provider.id)
                                        }
                                    />
                                    {provider.name}
                                </label>
                            ),
                        )}
                    </div>
                </SettingsSection>
            </div>
        </div>
    );
};
